import React, { Fragment } from 'react';
import { Card } from 'reactstrap';
import { EventSectionSaleProductReport } from '@/model/EventSectionSaleProductReport';
import { CustomTable, TableColumn } from '@/components/Table';
import { toCurrency } from '@/helpers/masks/toCurrency';
import { EventSectionProductReport } from '@/model/EventSectionProductReport';
import { EventSectionMachineReport } from '@/model/EventSectionMachineReport';

interface StateProps {
  sections: EventSectionSaleProductReport[];
}

export const SaleProductSectionsContent: React.FC<StateProps> = ({ sections }): JSX.Element => {
  const getColumns = (products: EventSectionProductReport[]): TableColumn<any>[] => {
    let showStock = false;
    let showCommission = false;

    const columns: TableColumn<any>[] = [
      {
        name: 'Produto',
        selector: row => row.productName,
      },
      {
        name: 'Vendidos',
        selector: row => row.productSold,
      },
      {
        name: 'Valor',
        selector: row => row.unitValue,
      },
      {
        name: 'Venda',
        selector: row => row.totalSaleValue,
      },
    ];

    // eslint-disable-next-line no-plusplus
    for (let p = 0; p < products.length; p++) {
      if (products[p].initialInventory > 0 || products[p].availableStock > 0) {
        showStock = true;
      }
      if (products[p].totalCommission > 0) {
        showCommission = true;
      }
      if (showStock && showCommission) {
        break;
      }
    }
    if (showCommission) {
      columns.push({
        name: 'Comissão',
        selector: row => row.totalCommission,
      });
      columns.push({
        name: 'Venda + Comissão',
        selector: row => row.totalSaleWithCommission,
      });
    }
    if (showStock) {
      columns.push({
        name: 'Estoque inicial',
        selector: row => row.initialInventory,
      });
      columns.push({
        name: 'Estoque disponível',
        selector: row => row.availableStock,
      });
    }
    return columns;
  };

  const getColumnMachines = (machines: EventSectionMachineReport[]): TableColumn<any>[] => {
    let showCourtesy = false;
    let showDiscount = false;
    let showCommission = false;
    const columns: TableColumn<any>[] = [
      {
        name: 'Serial',
        selector: row => row.serialNumber,
      },
      {
        name: 'Vendidos',
        selector: row => row.amountSold,
      },
    ];
    // eslint-disable-next-line no-plusplus
    for (let m = 0; m < machines.length; m++) {
      if (machines[m].courtesies > 0) {
        showCourtesy = true;
      }
      if (machines[m].discount > 0) {
        showDiscount = true;
      }
      if (machines[m].totalCommission > 0) {
        showCommission = true;
      }
      if (showCourtesy && showDiscount && showCommission) {
        break;
      }
    }
    if (showCourtesy) {
      columns.push({
        name: 'Cortesias',
        selector: row => row.courtesies,
      });
    }
    if (showDiscount) {
      columns.push({
        name: 'Desconto',
        selector: row => row.discount,
      });
    }
    columns.push({
      name: 'Venda',
      selector: row => row.totalSaleValue,
    });
    if (showCommission) {
      columns.push({
        name: 'Comissão',
        selector: row => row.totalCommission,
      });
      columns.push({
        name: 'Venda + Comissão',
        selector: row => row.totalSaleWithCommission,
      });
    }
    return columns;
  };

  return (
    <Fragment>
      <h6 className="mb-4 collapseTableText">Vendas de produtos por setor</h6>
      <hr />
      {sections.map(data => (
        <Fragment key={data.id}>
          <h6 className="mb-4 mt-5 collapseTableText">{data.name}</h6>
          <Card
            className="mb-5 card-no-border customTableScroll"
            style={{ border: 'none', overflow: 'auto' }}
          >
            {data.categories.map(category => (
              <Fragment key={category.id}>
                <h6 className="collapseTableText">{category.name}</h6>
                <div className="p-4">
                  <CustomTable
                    theme="secondary"
                    numberRowsPerPage={0}
                    progressPending={false}
                    columns={getColumns(category.products)}
                    data={
                      category.products.map(product => ({
                        productName: product.name,
                        productSold: product.amountSold,
                        initialInventory: product.initialInventory,
                        availableStock: product.availableStock,
                        unitValue: toCurrency(product.unitValue),
                        totalSaleValue: toCurrency(product.totalSaleValue),
                        totalCommission: toCurrency(product.totalCommission),
                        totalSaleWithCommission: toCurrency(product.totalSaleWithCommission),
                      })) || []
                    }
                  />
                </div>
              </Fragment>
            ))}
            <h6 className="collapseTableText">Maquininhas</h6>
            <div className="p-4">
              <CustomTable
                theme="secondary"
                numberRowsPerPage={0}
                progressPending={false}
                columns={getColumnMachines(data.machines)}
                data={
                  data.machines.map(machine => ({
                    serialNumber: machine.serialNumber,
                    amountSold: machine.amountSold,
                    courtesies: machine.courtesies,
                    discount: toCurrency(machine.discount),
                    totalSaleValue: toCurrency(machine.totalSaleValue),
                    totalCommission: toCurrency(machine.totalCommission),
                    totalSaleWithCommission: toCurrency(machine.totalSaleWithCommission),
                  })) || []
                }
              />
            </div>
            <hr className="mb-0" style={{ minWidth: '1080px' }} />
            <div
              style={{
                padding: '30px 40px',
                gap: '40px',
              }}
              className="d-flex justify-content-center"
            >
              <div className="d-flex">
                <div className="mr-1 text-small-gray-regular">Quantidade:</div>
                <div className="text-small-black-600">{data.summary.amountSold}</div>
              </div>
              {data.summary.courtesies > 0 && (
                <div className="d-flex">
                  <div className="mr-1 text-small-gray-regular">Cortesias:</div>
                  <div className="text-small-black-600">{data.summary.courtesies}</div>
                </div>
              )}
              {data.summary.discount > 0 && (
                <div className="d-flex">
                  <div className="mr-1 text-small-gray-regular">Descontos:</div>
                  <div className="text-small-black-600">{toCurrency(data.summary.discount)}</div>
                </div>
              )}
              {data.summary.initialInventory > 0 && (
                <div className="d-flex">
                  <div className="mr-1 text-small-gray-regular">Estoque inicial:</div>
                  <div className="text-small-black-600">{data.summary.initialInventory}</div>
                </div>
              )}
              {data.summary.availableStock > 0 && (
                <div className="d-flex">
                  <div className="mr-1 text-small-gray-regular">Estoque disponível:</div>
                  <div className="text-small-black-600">{data.summary.availableStock}</div>
                </div>
              )}
              <div className="d-flex">
                <div className="mr-1 text-small-gray-regular">Venda:</div>
                <div className="text-small-black-600">
                  {toCurrency(data.summary.totalSaleValue)}
                </div>
              </div>
              {data.summary.totalCommission > 0 && (
                <Fragment>
                  <div className="d-flex">
                    <div className="mr-1 text-small-gray-regular">Comissão:</div>
                    <div className="text-small-black-600">
                      {toCurrency(data.summary.totalCommission)}
                    </div>
                  </div>
                  <div className="d-flex">
                    <div className="mr-1 text-small-gray-regular">Venda + Comissão:</div>
                    <div className="text-small-black-600">
                      {toCurrency(data.summary.totalSaleWithCommission)}
                    </div>
                  </div>
                </Fragment>
              )}
            </div>
            <div
              style={{
                padding: '30px 40px',
                gap: '40px',
              }}
              className="d-flex justify-content-center"
            >
              <div className="d-flex">
                <div className="mr-1 text-small-gray-regular">Crédito:</div>
                <div className="text-small-black-600">{toCurrency(data.payments.credit)}</div>
              </div>
              <div className="d-flex">
                <div className="mr-1 text-small-gray-regular">Débito:</div>
                <div className="text-small-black-600">{toCurrency(data.payments.debit)}</div>
              </div>
              <div className="d-flex">
                <div className="mr-1 text-small-gray-regular">PIX:</div>
                <div className="text-small-black-600">{toCurrency(data.payments.pix)}</div>
              </div>
              <div className="d-flex">
                <div className="mr-1 text-small-gray-regular">Dinheiro:</div>
                <div className="text-small-black-600">{toCurrency(data.payments.money)}</div>
              </div>
            </div>
          </Card>
        </Fragment>
      ))}
    </Fragment>
  );
};
